import React, {useEffect, useImperativeHandle, useRef} from "react";

import {useFrame} from "@react-three/fiber";
import type {InstancedMesh} from "three";

import {ParticleManager} from "./ParticleManager";
import type {
  EmitOptions,
  ParticleEffectProps,
  ParticleEffectRef,
} from "./types";

const ParticleEffect = React.forwardRef<ParticleEffectRef, ParticleEffectProps>(
  (props, ref) => {
    const {maxParticles = 500, children} = props;
    const meshRef = useRef<InstancedMesh>(null!);
    const manager = useRef<ParticleManager>(null);

    // Initialize or re-initialize the manager if props change
    if (!manager.current) {
      manager.current = new ParticleManager(props);
    }

    useEffect(() => {
      // Re-create manager if critical properties like maxParticles change
      manager.current = new ParticleManager(props);
      if (meshRef.current) {
        manager.current.initializeMesh(meshRef.current);
      }
    }, [props]);

    useFrame((_, delta) => {
      manager.current?.update(delta, meshRef.current);
    });

    useImperativeHandle(ref, () => ({
      emit: (options: EmitOptions) => {
        // Limit spawn count to available particles to avoid overwhelming the system
        const count = Math.min(options.count, maxParticles);
        manager.current?.emit({...options, count});
      },
    }));

    return (
      <instancedMesh
        ref={meshRef}
        args={[undefined, undefined, maxParticles]}
        frustumCulled={false}
      >
        {children}
      </instancedMesh>
    );
  },
);

export default ParticleEffect;
